import { makeAutoObservable } from "mobx";
import { ICompareValueStore } from "./compare-value-factory";
import WindowCompareValueField from "../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/compare-value-type-fields/window-compare-value-field";
import { CONDITION_OPERATORS, IConditionFieldPreset } from "../../policy-types";
import { toNumber } from "../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/helpers/type-convertions";

export interface IValueTypeCompareValue {
    value: number;
    type: string;
}

class WindowCompareValueStore implements ICompareValueStore<IValueTypeCompareValue> {
    private _model: IValueTypeCompareValue = {} as IValueTypeCompareValue;
    private _changes: Partial<IValueTypeCompareValue> = {};
    private _uncommittedChanges: Partial<IValueTypeCompareValue> = {};

    constructor(private _fieldPreset: IConditionFieldPreset, compareValue?: string) {
        makeAutoObservable(this);
        if (compareValue) {
            this._model = this._deserializeModel(compareValue);
        }
    }

    public setChanges(changes?: string) {
        if (changes) {
            this._changes = this._deserializeModel(changes);
        }
    }

    get options() {
        return this._fieldPreset.compare_value_data.map(v => {
            return {
                value: v as string,
                label: v as string
            }
        });
    }

    get Component() {
        return WindowCompareValueField;
    }

    private _deserializeModel(compareValue: string): IValueTypeCompareValue {
        let model = {} as IValueTypeCompareValue;
        try {
            model = JSON.parse(compareValue);
        }
        catch(e) {
            /** should never happen */
            console.error(`invalid condition's compare value from DB: ${compareValue}`);
        }
        return model;
    }

    public onOperatorChanged(op: CONDITION_OPERATORS): void {
    }

    public get value() {
        return this._uncommittedChanges.value ?? this._changes.value ?? this._model.value;
    }

    public get type() {
        return this._uncommittedChanges.type ?? this._changes.type ?? this._model.type;
    }

    public setValue(v: number) {
        this._uncommittedChanges.value = toNumber(v);
    }

    public setType(v: string) {
        this._uncommittedChanges.type = v;
    }

    public commitChanges(): void {
        this._changes = {...this._changes, ...this._uncommittedChanges};
        this._clearUnneededChanges();
    }

    public discardUncommittedChanges(): void {
        this._uncommittedChanges = {};
    }

    public hasChanges(): boolean {
        return Object.values(this._changes).length > 0;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public getUpdatedModel(): string|undefined {
        /** if empty serialized - return undefined */
        const serialized = JSON.stringify({ ...this._model, ...this._changes });
        if (serialized === JSON.stringify({})) {
            return undefined;
        }
        return serialized;
    }

    public getUncommittedChanges(): IValueTypeCompareValue {
        return { ...this._model, ...this._changes, ...this._uncommittedChanges };
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IValueTypeCompareValue;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default WindowCompareValueStore;
